import { Events, emit, on } from './events.js';

/**
 * CartDiscountCode - Handles discount code application in cart
 *
 * Strategy:
 * - Single source of truth: cart.discount_codes from /cart.js
 * - Apply via /discount/CODE with credentials: same-origin
 * - Fail silently on invalid codes
 * - Always render from cart.discount_codes only
 */
class CartDiscountCode extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-discount-input]');
    this.applyBtn = this.querySelector('[data-apply-btn]');
    this.btnText = this.querySelector('[data-btn-text]');
    this.spinner = this.querySelector('[data-spinner]');
    this.appliedDiscountsContainer = this.querySelector('[data-applied-discounts]');
    this.errorContainer = this.querySelector('[data-discount-error]');
    this.errorDismissBtn = this.querySelector('[data-dismiss-error]');

    // Bind events
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', () => this.handleApply());
    }

    if (this.input) {
      this.input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleApply();
        }
      });
      // Hide error when user starts typing
      this.input.addEventListener('input', () => this.hideError());
    }

    // Bind dismiss error button
    if (this.errorDismissBtn) {
      this.errorDismissBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideError();
      });
    }

    // Listen to cart updates from other sources
    this._unsubs = [
      on(Events.cart.update, (event) => {
        this.renderFromCart(event.detail?.cart);
      })
    ];

    // Initial load - fetch cart and render
    this.init();
  }

  async init() {
    try {
      const cart = await this.fetchCart();
      this.renderFromCart(cart);
    } catch (e) {
      console.error('[cart-discount] Init error:', e);
    }
  }

  async handleApply() {
    const code = this.input?.value?.trim().toUpperCase();
    if (!code) return;

    this.setLoading(true);
    this.hideError();

    try {
      // Get current codes before applying
      const cartBefore = await this.fetchCart();
      const codesBefore = (cartBefore.discount_codes || [])
        .filter(dc => dc.applicable !== false)
        .map(dc => dc.code.toUpperCase());

      // Apply discount via Shopify's /discount endpoint
      await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart`, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow'
      });

      // Small delay to ensure session is updated
      await new Promise(resolve => setTimeout(resolve, 300));

      // Fetch cart as single source of truth
      const cart = await this.fetchCart();

      // Check if code was actually applied
      const codesAfter = (cart.discount_codes || [])
        .filter(dc => dc.applicable !== false)
        .map(dc => dc.code.toUpperCase());

      const wasApplied = codesAfter.includes(code) && !codesBefore.includes(code);

      if (!wasApplied) {
        // Code was not applied - show error
        this.showError();
        if (this.input) this.input.value = '';
        return;
      }

      // Render from cart
      this.renderFromCart(cart);

      // Emit cart update for other components
      emit(Events.cart.update, { cart }, this);

      // Clear input on success
      if (this.input) this.input.value = '';

    } catch (e) {
      console.error('[cart-discount] Apply error:', e);
      this.showError();
    } finally {
      this.setLoading(false);
    }
  }

  async handleRemove(codeToRemove) {
    this.setLoading(true);

    try {
      // Get current discount codes (excluding the one to remove)
      const currentCart = await this.fetchCart();
      const codesToKeep = (currentCart.discount_codes || [])
        .filter(dc => dc.applicable !== false && dc.code.toUpperCase() !== codeToRemove.toUpperCase())
        .map(dc => dc.code);

      // Clear all discounts first
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ discount: '' })
      });

      // Re-add remaining codes one by one
      for (const code of codesToKeep) {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ discount: code })
        });
      }

      // Fetch final cart state
      const cart = await this.fetchCart();

      // Render from cart
      this.renderFromCart(cart);

      // Emit cart update for other components
      emit(Events.cart.update, { cart }, this);

      console.log('[cart-discount] Removed discount:', codeToRemove, 'Kept:', codesToKeep);

    } catch (e) {
      console.error('[cart-discount] Remove error:', e);
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }

    return await response.json();
  }

  renderFromCart(cart) {
    if (!this.appliedDiscountsContainer || !cart) return;

    // Clear container
    this.appliedDiscountsContainer.innerHTML = '';

    // Get discount codes from cart (single source of truth)
    const discountCodes = cart.discount_codes || [];

    // Show all discount codes (even if amount is 0 when cart is empty)
    // Only filter out codes explicitly marked as not applicable
    const appliedCodes = discountCodes.filter(dc => dc.applicable !== false);

    // Render each applied discount
    appliedCodes.forEach(discount => {
      const badge = this.createDiscountBadge(discount, cart);
      this.appliedDiscountsContainer.appendChild(badge);
    });

    console.log('[cart-discount] Rendered', appliedCodes.length, 'discount(s) from cart');
  }

  createDiscountBadge(discount, cart) {
    const badge = document.createElement('div');
    badge.className = 'text-sm flex items-center justify-between py-1.5 px-3 bg-[var(--discount-bg,#F7ECE0)] rounded-full';
    badge.style.cssText = '--discount-bg:#F7ECE0; --discount-icon:#FAA61A; --discount-color:#064833;';

    const code = discount.code || discount.title || '';

    // Get discount amount - try multiple sources
    let amount = 0;

    // 1. Try discount.amount directly (from discount_codes)
    if (discount.amount && discount.amount > 0) {
      amount = discount.amount;
    }

    // 2. If not available, try matching in cart_level_discount_applications
    if (amount === 0 && cart.cart_level_discount_applications) {
      const application = cart.cart_level_discount_applications.find(app => {
        const appTitle = (app.title || '').toLowerCase().trim();
        const appKey = (app.key || '').toLowerCase().trim();
        const codeLC = code.toLowerCase().trim();
        return appTitle === codeLC || appKey === codeLC || appTitle.includes(codeLC) || codeLC.includes(appTitle);
      });

      if (application && application.total_allocated_amount) {
        amount = application.total_allocated_amount;
      }
    }

    // 3. If still no amount and only one discount, use cart.total_discount
    if (amount === 0 && cart.total_discount && cart.total_discount > 0) {
      const discountCodes = cart.discount_codes || [];
      const appliedCodes = discountCodes.filter(dc => dc.applicable === true);
      if (appliedCodes.length === 1) {
        amount = cart.total_discount;
      }
    }

    console.log('[cart-discount] createDiscountBadge', { code, amount, discountObj: discount, cartTotalDiscount: cart.total_discount });

    // Format amount with parentheses next to code
    const formattedAmount = this.formatMoney(amount, cart.currency);

    badge.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-[var(--discount-icon)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
        <span class="font-medium text-[var(--discount-color)]">${code}${amount > 0 ? ` <span class="opacity-75">(-${formattedAmount})</span>` : ''}</span>
      </div>
      <button type="button" class="flex items-center justify-center w-6 h-6 rounded-full hover:bg-black/10 transition-colors" data-remove-discount="${code}" aria-label="Remove discount">
        <svg class="w-4 h-4 text-[var(--discount-color)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `;

    // Bind remove button
    const removeBtn = badge.querySelector('[data-remove-discount]');
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleRemove(code);
      });
    }

    return badge;
  }

  formatMoney(cents, currency = 'EUR') {
    try {
      const amount = cents / 100;
      // Use European locale to get comma as decimal separator
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Fallback formatting with comma
      const symbol = currency === 'EUR' ? '€' : '$';
      return `${symbol}${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  }

  setLoading(loading) {
    if (this.applyBtn) this.applyBtn.disabled = loading;
    if (this.btnText) this.btnText.classList.toggle('hidden', loading);
    if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
    if (this.input) this.input.disabled = loading;
  }

  showError() {
    if (this.errorContainer) {
      this.errorContainer.classList.remove('hidden');
    }
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.classList.add('hidden');
    }
  }

  disconnectedCallback() {
    (this._unsubs || []).forEach(un => {
      try {
        un?.();
      } catch (_) {}
    });
  }
}

// Register custom element
try {
  if (!customElements.get('cart-discount-code')) {
    customElements.define('cart-discount-code', CartDiscountCode);
  }
} catch (_) {}

export default CartDiscountCode;
